import { EditOutlined } from "@ant-design/icons";
import { Button, Empty, Table, TableProps, Typography } from "antd";
import React from "react";

import { Popup } from "../../atoms/Popup";
import SectionHeader from "../../molecules/SectionHeader";

import { CatalogueSidebar } from "./CatalogueSidebar";
import { MarketingConfigurationForm } from "./MarketingConfigurationForm";
import { PublicListUrlPanel } from "./PublicListUrlPanel";
import { getColumnsForCatalogue } from "./tableColumns";
import { MarketingConfigurationItem } from "./types";
import { useMarketingConfigurationModule } from "./useMarketingConfigurationModule";

export const MarketingConfigurationModule = () => {
  const {
    orgId,
    organizations,
    organizationOptions,
    changeSelectedOrgId,
    catalogueId,
    catalogue,
    catalogues,
    changeCategory,
    items,
    marketingPublicListUrl,
    enableMarketingPublicList,
    existingTimeSlots,
    existingFeedbackQuestions,
    isLoading,
    isSaving,
    modalMode,
    editingRecord,
    methods,
    handleAdd,
    handleEdit,
    handleSavePublicListUrl,
    handleSubmit,
    resetForm,
  } = useMarketingConfigurationModule();

  const isViewingDay = catalogueId === "viewingDaysConfig";
  const isPublicListUrl = catalogueId === "marketingPublicListUrl";
  const hideAddNew = isViewingDay || isPublicListUrl;

  const columns: TableProps<MarketingConfigurationItem>["columns"] = [
    ...getColumnsForCatalogue(catalogueId),
    {
      title: "ACTIONS",
      key: "actions",
      width: 90,
      render: (_: unknown, record: MarketingConfigurationItem) => (
        <Button type="link" onClick={() => handleEdit(record)} style={{ padding: 0 }}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <>
      <SectionHeader
        heading="Marketing Configuration"
        addNewAction={hideAddNew ? undefined : handleAdd}
        organizationList={organizations}
        setOrgId={changeSelectedOrgId}
        orgFilterInitialValue={{ value: orgId }}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "220px 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <CatalogueSidebar catalogues={catalogues} catalogueId={catalogueId} onChange={changeCategory} />

        {isPublicListUrl ? (
          <PublicListUrlPanel
            orgId={orgId}
            url={marketingPublicListUrl}
            isLoading={isLoading && !isSaving}
            isSaving={isSaving}
            onSave={handleSavePublicListUrl}
          />
        ) : (
          <Table
            rowKey={(record) => record.uid}
            className="custom-table org_table_wrap"
            columns={columns}
            dataSource={items}
            pagination={false}
            loading={isLoading}
            locale={{
              emptyText: (
                <div
                  style={{
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Empty description={orgId ? "No items yet" : "Select an organization"} />
                </div>
              ),
            }}
          />
        )}
      </div>

      <Popup
        closable
        visibility={Boolean(modalMode) && !isPublicListUrl}
        title={
          <Typography.Title level={3}>
            {modalMode === "edit" ? "Edit" : "Add"} {catalogue.title}
          </Typography.Title>
        }
        content={
          modalMode && !isPublicListUrl ? (
            <MarketingConfigurationForm
              formType={modalMode}
              catalogueId={catalogueId}
              methods={methods}
              organizationOptions={organizationOptions}
              existingTimeSlots={existingTimeSlots}
              existingFeedbackQuestions={existingFeedbackQuestions}
              existingItems={items}
              enableMarketingPublicList={enableMarketingPublicList}
              editingUid={editingRecord?.uid}
              onSubmit={handleSubmit}
              onCancel={resetForm}
            />
          ) : null
        }
        width={520}
        onCancel={resetForm}
      />
    </>
  );
};
